// src/components/padrekko/qc/PersistentMeasurementsLayer.jsx
//
// Renders the case's saved map measurements (distance lines / area polygons)
// onto the shared MapLibre map, plus hover metadata and a click-to-select panel
// with delete. Mounts only inside a PadRekko case context (gated by the
// caller); on unmount it removes its sources + layers, so switching to Explore
// mode reverts the map to the plain ephemeral ruler.
//
// Style is deliberately distinct from the ephemeral ruler (teal): persistent
// measurements are amber with a dark halo — muted enough not to fight the
// imagery, bold enough to read. Each carries an inline value label ("37 m").

import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { Loader2, Trash2, Ruler, X as XIcon } from "lucide-react";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import { useQcStore } from "../../../stores/useQcStore";

const SRC = "pk-measure";
const SRC_LABEL = "pk-measure-label";
const SRC_ARROW = "pk-measure-arrow";
const AMBER = "#f59e0b";

// Arrowhead chevron at the head of a tail→head line. Size scales with the line
// (capped) so it reads at typical zooms — a map-appropriate stand-in for the
// photo overlay's SVG arrow marker. Returns a 3-point line [wingA, head, wingB].
function arrowheadLine(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const tail = coords[0], head = coords[coords.length - 1];
  const dx = head[0] - tail[0], dy = head[1] - tail[1];
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len; // unit tail→head
  const size = Math.min(len * 0.11, 0.0003); // ~half the previous wing length, capped
  const ang = (18 * Math.PI) / 180;          // narrower spread from the shaft
  // back-pointing unit vector rotated by ±ang
  const wing = (a) => [
    -(ux * Math.cos(a) - uy * Math.sin(a)),
    -(ux * Math.sin(a) + uy * Math.cos(a)),
  ];
  const l = wing(ang), r = wing(-ang);
  return [
    [head[0] + l[0] * size, head[1] + l[1] * size],
    head,
    [head[0] + r[0] * size, head[1] + r[1] * size],
  ];
}

function labelPoint(geom) {
  if (!geom) return null;
  if (geom.type === "Point") return geom.coordinates;
  if (geom.type === "LineString") {
    const c = geom.coordinates;
    const a = c[0], b = c[c.length - 1];
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  }
  if (geom.type === "Polygon") {
    const ring = geom.coordinates[0] || [];
    const pts = ring.slice(0, -1); // drop closing dup
    if (!pts.length) return null;
    let x = 0, y = 0;
    for (const p of pts) { x += p[0]; y += p[1]; }
    return [x / pts.length, y / pts.length];
  }
  return null;
}

function targetLabel(a) {
  if (a.finding_id) return "finding";
  if (a.pad_id) return "pad";
  return "case";
}

export default function PersistentMeasurementsLayer() {
  const mapInstance = useAppViewStore((s) => s.mapInstance);
  const annotations = useQcStore((s) => s.mapAnnotations);
  const deleteMapAnnotation = useQcStore((s) => s.deleteMapAnnotation);
  const deleteInflightId = useQcStore((s) => s.mapAnnotationDeleteInflightId);

  const [selected, setSelected] = useState(null); // the clicked annotation's props
  const popupRef = useRef(null);

  const fc = useMemo(() => ({
    type: "FeatureCollection",
    features: (annotations ?? [])
      .filter((a) => a.geometry)
      .map((a) => ({
        type: "Feature",
        id: a.id,
        geometry: a.geometry,
        properties: {
          id: a.id,
          value: a.measurement_value,
          atype: a.annotation_type,
          label: a.label || "",
          creator_name: a.creator_name || "",
          created_at: a.created_at || "",
          target: targetLabel(a),
          can_delete: a.can_delete === true,
        },
      })),
  }), [annotations]);

  const labelFc = useMemo(() => ({
    type: "FeatureCollection",
    features: (annotations ?? [])
      .map((a) => {
        const pt = labelPoint(a.geometry);
        // distance/area show their measurement value; text shows its content;
        // arrow shows its optional caption (if any).
        const text = a.measurement_value || a.label || "";
        return pt && text
          ? { type: "Feature", geometry: { type: "Point", coordinates: pt }, properties: { value: text } }
          : null;
      })
      .filter(Boolean),
  }), [annotations]);

  // Arrowhead chevrons for arrow annotations.
  const arrowFc = useMemo(() => ({
    type: "FeatureCollection",
    features: (annotations ?? [])
      .filter((a) => a.annotation_type === "arrow" && a.geometry?.type === "LineString")
      .map((a) => {
        const head = arrowheadLine(a.geometry.coordinates);
        return head
          ? { type: "Feature", geometry: { type: "LineString", coordinates: head }, properties: { id: a.id } }
          : null;
      })
      .filter(Boolean),
  }), [annotations]);

  // Add sources + layers once the map is ready; remove on unmount.
  useEffect(() => {
    if (!mapInstance) return undefined;

    function ensure() {
      if (!mapInstance.getSource(SRC)) {
        mapInstance.addSource(SRC, { type: "geojson", data: fc });
      }
      if (!mapInstance.getSource(SRC_LABEL)) {
        mapInstance.addSource(SRC_LABEL, { type: "geojson", data: labelFc });
      }
      if (!mapInstance.getSource(SRC_ARROW)) {
        mapInstance.addSource(SRC_ARROW, { type: "geojson", data: arrowFc });
      }
      if (!mapInstance.getLayer("pk-measure-fill")) {
        mapInstance.addLayer({
          id: "pk-measure-fill", type: "fill", source: SRC,
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: { "fill-color": AMBER, "fill-opacity": 0.12 },
        });
      }
      if (!mapInstance.getLayer("pk-measure-halo")) {
        mapInstance.addLayer({
          id: "pk-measure-halo", type: "line", source: SRC,
          paint: { "line-color": "#000", "line-opacity": 0.35, "line-width": 5 },
        });
      }
      if (!mapInstance.getLayer("pk-measure-line")) {
        mapInstance.addLayer({
          id: "pk-measure-line", type: "line", source: SRC,
          paint: { "line-color": AMBER, "line-width": 2.5 },
        });
      }
      // Arrowhead chevron (separate source so it doesn't affect line styling).
      if (!mapInstance.getLayer("pk-measure-arrow")) {
        mapInstance.addLayer({
          id: "pk-measure-arrow", type: "line", source: SRC_ARROW,
          layout: { "line-cap": "round", "line-join": "round" },
          paint: { "line-color": AMBER, "line-width": 2.5 },
        });
      }
      // Text-anchor dot (Point geometries only — i.e. text annotations).
      if (!mapInstance.getLayer("pk-measure-dot")) {
        mapInstance.addLayer({
          id: "pk-measure-dot", type: "circle", source: SRC,
          filter: ["==", ["geometry-type"], "Point"],
          paint: {
            "circle-radius": 4, "circle-color": AMBER,
            "circle-stroke-color": "#000", "circle-stroke-width": 1,
          },
        });
      }
      if (!mapInstance.getLayer("pk-measure-label")) {
        mapInstance.addLayer({
          id: "pk-measure-label", type: "symbol", source: SRC_LABEL,
          layout: {
            "text-field": ["get", "value"],
            "text-size": 12,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-allow-overlap": false,
          },
          paint: {
            "text-color": "#fde68a", "text-halo-color": "#000", "text-halo-width": 1.6,
          },
        });
      }
    }
    ensure();
    mapInstance.on("styledata", ensure);

    return () => {
      mapInstance.off("styledata", ensure);
      for (const id of ["pk-measure-label", "pk-measure-dot", "pk-measure-arrow", "pk-measure-line", "pk-measure-halo", "pk-measure-fill"]) {
        if (mapInstance.getLayer(id)) mapInstance.removeLayer(id);
      }
      if (mapInstance.getSource(SRC_ARROW)) mapInstance.removeSource(SRC_ARROW);
      if (mapInstance.getSource(SRC_LABEL)) mapInstance.removeSource(SRC_LABEL);
      if (mapInstance.getSource(SRC)) mapInstance.removeSource(SRC);
    };
  }, [mapInstance]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push data on change.
  useEffect(() => {
    if (!mapInstance) return;
    mapInstance.getSource(SRC)?.setData(fc);
    mapInstance.getSource(SRC_LABEL)?.setData(labelFc);
    mapInstance.getSource(SRC_ARROW)?.setData(arrowFc);
  }, [mapInstance, fc, labelFc, arrowFc]);

  // Highlight the selected feature (widen the line).
  useEffect(() => {
    if (!mapInstance || !mapInstance.getLayer("pk-measure-line")) return;
    mapInstance.setPaintProperty("pk-measure-line", "line-width", [
      "case", ["==", ["get", "id"], selected?.id ?? ""], 4, 2.5,
    ]);
  }, [mapInstance, selected]);

  // Hover popup (metadata) + click to select.
  useEffect(() => {
    if (!mapInstance) return undefined;
    const hitLayers = ["pk-measure-fill", "pk-measure-line", "pk-measure-label", "pk-measure-dot"];

    function onEnter(e) {
      mapInstance.getCanvas().style.cursor = "pointer";
      const p = e.features?.[0]?.properties;
      if (!p) return;
      const html =
        `<div style="font:11px sans-serif;color:#1f2937">` +
        `<b>${escapeHtml(p.value ?? "")}</b>${p.label ? ` · ${escapeHtml(p.label)}` : ""}<br/>` +
        `<span style="color:#6b7280">${escapeHtml(p.target)} · by ${escapeHtml(p.creator_name || "—")}</span>` +
        `</div>`;
      popupRef.current?.remove();
      popupRef.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 8 })
        .setLngLat(e.lngLat).setHTML(html).addTo(mapInstance);
    }
    function onLeave() {
      mapInstance.getCanvas().style.cursor = "";
      popupRef.current?.remove();
      popupRef.current = null;
    }
    function onClick(e) {
      const p = e.features?.[0]?.properties;
      if (!p) return;
      setSelected({
        id: p.id, value: p.value, label: p.label, target: p.target,
        creator_name: p.creator_name, created_at: p.created_at,
        can_delete: p.can_delete === true || p.can_delete === "true",
      });
    }
    for (const id of hitLayers) {
      mapInstance.on("mouseenter", id, onEnter);
      mapInstance.on("mouseleave", id, onLeave);
      mapInstance.on("click", id, onClick);
    }
    return () => {
      for (const id of hitLayers) {
        mapInstance.off("mouseenter", id, onEnter);
        mapInstance.off("mouseleave", id, onLeave);
        mapInstance.off("click", id, onClick);
      }
      popupRef.current?.remove();
      popupRef.current = null;
    };
  }, [mapInstance]);

  async function handleDelete() {
    if (!selected) return;
    const result = await deleteMapAnnotation(selected.id);
    if (result.ok) setSelected(null);
  }

  if (!selected) return null;

  // Selected-measurement panel — metadata + delete.
  return (
    <div className="absolute z-[90] bottom-4 left-1/2 -translate-x-1/2
                    bg-bg-soft/95 border border-amber-500/50 rounded-lg shadow-2xl
                    px-3 py-2 flex items-center gap-3 text-[11px]">
      <Ruler size={13} className="text-amber-400 shrink-0" />
      <div className="min-w-0">
        <div className="text-slate-100 font-medium">
          {selected.value}{selected.label ? <span className="text-slate-400"> · {selected.label}</span> : null}
        </div>
        <div className="text-[10px] text-fg-secondary">
          {selected.target} · by {selected.creator_name || "—"}
        </div>
      </div>
      {selected.can_delete && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteInflightId === selected.id}
          className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                     bg-red-500/10 border-red-500/40 text-red-300 hover:bg-red-500/20
                     disabled:opacity-40 transition shrink-0"
          title="Delete this measurement"
        >
          {deleteInflightId === selected.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
          Delete
        </button>
      )}
      <button
        type="button"
        onClick={() => setSelected(null)}
        className="text-fg-secondary hover:text-slate-200 transition shrink-0"
        title="Close"
      >
        <XIcon size={13} />
      </button>
    </div>
  );
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
